/* iter-52a — Compact currency selector dropdown.
 *
 * Drop into any header. Reads/writes through the CurrencyContext so
 * the choice propagates instantly.
 */
import React from 'react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Coins, Check } from 'lucide-react';

export default function CurrencySelector({ variant = 'compact', mobile = false }) {
  const { currency, setCurrency, supported, ratesFetchedAt } = useCurrency();
  if (!supported.length) return null;

  const current = supported.find(c => c.code === currency) || supported[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={
            mobile
              ? 'h-11 gap-1.5 px-3 text-[13px]'
              : 'h-7 gap-1.5 px-2 text-[12px]'
          }
          data-testid="currency-selector-button"
          title="Choose display currency"
        >
          <Coins className={mobile ? 'h-4 w-4 text-muted-foreground' : 'h-3.5 w-3.5 text-muted-foreground'} />
          <span className="font-mono-stat">{current.code}</span>
          <span className="text-muted-foreground">{current.symbol}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]" data-testid="currency-selector-menu">
        <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          Display currency
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={currency} onValueChange={setCurrency}>
          {supported.map(c => (
            <DropdownMenuRadioItem
              key={c.code}
              value={c.code}
              className="gap-2 text-[12.5px]"
              data-testid={`currency-option-${c.code}`}
            >
              <span className="inline-flex w-6 justify-center text-muted-foreground">{c.symbol}</span>
              <span className="font-mono-stat">{c.code}</span>
              <span className="text-muted-foreground">{c.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {variant !== 'compact' && ratesFetchedAt && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[10px] text-muted-foreground">
              FX rates as of {new Date(ratesFetchedAt).toLocaleString()}
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        <div className="flex items-center gap-1 px-2 py-1 text-[10px] text-muted-foreground">
          <Check className="h-2.5 w-2.5" /> Charged in USD at checkout
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
